import { useCallback } from 'react';
import Text from 'common/ui/Text';
import { Stack, Spacer } from 'common/ui/Layout';
import SettingsDropdown from 'common/SettingsDropdown';
import { useDefaultThumbnailSizeSetting } from 'hooks/useViewerSettings';
import { SettingsMenuItem } from 'common/SettingsDropdown/SettingsDropdown';
import { telemetryContextFor } from 'modules/Apollo/types';

/** Thumbnail preset sizes. The small/large definitions here will determine the min/max resize
 * height. */
export const THUMBNAIL_PRESET_SIZES_PX = Object.freeze({
  SMALL: 115,
  NORMAL: 135,
  LARGE: 285,
});
const THUMBNAIL_SIZE_FIELD_LABEL = 'Default Thumbnail Size';
export const THUMBNAIL_SIZE_CUSTOM_DROPDOWN_VALUE = { name: 'Custom', value: -1 } as const;
export const THUMBNAIL_SIZE_DROPDOWN = [
  { name: 'Small', value: THUMBNAIL_PRESET_SIZES_PX.SMALL },
  { name: 'Normal', value: THUMBNAIL_PRESET_SIZES_PX.NORMAL },
  { name: 'Large', value: THUMBNAIL_PRESET_SIZES_PX.LARGE },
] as const;

/** Given a value, attempts to find a matching value for the dropdown. If not found, then it should
 * return the "Custom" preset */
export const valueToThumbnailSizePreset = (sizeValue?: number | null): number => {
  if (sizeValue == null) return THUMBNAIL_PRESET_SIZES_PX.NORMAL;
  return sizeValue;
};

export const ThumbnailSizeSettings = (): React.ReactElement => {
  const [thumbnailSizePx, setDefaultThumbnailSize] = useDefaultThumbnailSizeSetting({
    context: APOLLO_QUERY_CONTEXT,
  });
  const handleThumbnailSizeChange = useCallback(
    async (eventValue: number) => {
      await setDefaultThumbnailSize(eventValue);
    },
    [setDefaultThumbnailSize]
  );

  return (
    <>
      <Stack alignY="center">
        <Text>{THUMBNAIL_SIZE_FIELD_LABEL}</Text>
        <Spacer />
        <Stack space="small" alignY="center">
          <SettingsDropdown
            value={valueToThumbnailSizePreset(thumbnailSizePx)}
            onSelectedChange={(value) => {
              if (value === THUMBNAIL_SIZE_CUSTOM_DROPDOWN_VALUE.value) return;
              handleThumbnailSizeChange(Number(value));
            }}
            data-testid="thumbnail-size-preference-dropdown"
          >
            {THUMBNAIL_SIZE_DROPDOWN.map((option) => (
              <SettingsMenuItem
                id={`thumbnail-size-preference-menuitem-${option.value}`}
                value={option.value}
                key={option.value}
                data-testid={`${option.value}-menu-item`}
              >
                {option.name}
              </SettingsMenuItem>
            ))}
          </SettingsDropdown>
        </Stack>
      </Stack>
    </>
  );
};

ThumbnailSizeSettings.displayName = 'ThumbnailSizeSettings';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ThumbnailSizeSettings);
